<template>
  <div class="public-report-page">
    <div class="hero">
      <h1>{{ report.sponsor.sponsorName || 'Sponsor Report' }}</h1>
      <p>Public sponsor campaign report with publishing links and progress.</p>
    </div>

    <section class="card">
      <h2>Filters</h2>
      <div class="filters-grid">
        <div class="field">
          <label for="campaign-filter">Campaign</label>
          <select id="campaign-filter" v-model="filters.campaignId">
            <option value="">All Campaigns</option>
            <option v-for="item in campaignOptions" :key="item.campaignId" :value="item.campaignId">{{ item.campaignName }}</option>
          </select>
        </div>
        <div class="field">
          <label for="date-from">Date From</label>
          <input id="date-from" v-model="filters.dateFrom" type="date" />
        </div>
        <div class="field">
          <label for="date-to">Date To</label>
          <input id="date-to" v-model="filters.dateTo" type="date" />
        </div>
        <div class="actions">
          <button class="btn" :disabled="loading" @click="loadReport">{{ loading ? 'Loading...' : 'Apply Filter' }}</button>
          <button class="btn clear" :disabled="loading" @click="clearFilters">Clear</button>
        </div>
      </div>
    </section>

    <section class="card" v-if="selectedCampaignSummary">
      <h2>Campaign Specific Total & Progress</h2>
      <div class="summary-grid campaign-summary-grid">
        <article class="stat">
          <span>Campaign</span>
          <strong>{{ selectedCampaignSummary.campaignName || '-' }}</strong>
        </article>
        <article class="stat">
          <span>Total Target</span>
          <strong>{{ selectedCampaignSummary.totalTarget }}</strong>
        </article>
        <article class="stat">
          <span>Submitted</span>
          <strong>{{ selectedCampaignSummary.submittedCount }}</strong>
        </article>
        <article class="stat">
          <span>Remaining</span>
          <strong>{{ selectedCampaignSummary.remainingCount }}</strong>
        </article>
        <article class="stat">
          <span>Progress</span>
          <strong>{{ selectedCampaignSummary.progressPercent }}%</strong>
        </article>
      </div>
    </section>

    <section class="card summary-card">
      <div class="summary-grid">
        <article class="stat">
          <span>Total Content Target</span>
          <strong>{{ summary.totalContentTarget }}</strong>
        </article>
        <article class="stat">
          <span>Total Submitted</span>
          <strong>{{ summary.totalSubmittedContent }}</strong>
        </article>
        <article class="stat">
          <span>Total Remaining</span>
          <strong>{{ summary.totalRemainingContent }}</strong>
        </article>
        <article class="stat">
          <span>Progress</span>
          <strong>{{ summary.progressPercent }}%</strong>
        </article>
      </div>
    </section>

    <section class="card">
      <h2>Published Campaign Links</h2>
      <div class="header-actions" style="display:flex; justify-content:flex-end; gap:8px; margin-top:-28px;">
        <button class="btn" @click="downloadXlsx">Export XLSX</button>
        <button class="btn" @click="downloadCsv">Export CSV</button>
      </div>
      <div v-if="uploads.length" class="table-wrap">
        <table class="upload-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Title</th>
              <th>Category</th>
              <th>YouTube</th>
              <th>YouTube Views</th>
              <th>Facebook</th>
              <th>FB Views</th>
              <th v-if="hasFacebookReach">FB Reach</th>
              <th>Upload Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in uploads" :key="`${item.campaignId}-${item.createdAt}-${item.title}`">
              <td>{{ item.campaignName || '-' }}</td>
              <td>{{ item.title || '-' }}</td>
              <td>{{ item.category || '-' }}</td>
              <td>
                <a v-if="item.youtubeLink" :href="item.youtubeLink" target="_blank" rel="noopener noreferrer">YouTube</a>
                <span v-else>-</span>
              </td>
              <td>{{ item.youtubeLink ? Number(item.youtubeViews || 0).toLocaleString() : '-' }}</td>
              <td>
                <a v-if="item.facebookLink" :href="item.facebookLink" target="_blank" rel="noopener noreferrer">Facebook</a>
                <span v-else>-</span>
              </td>
              <td>{{ item.facebookLink ? Number(item.facebookViews || 0).toLocaleString() : '-' }}</td>
              <td v-if="hasFacebookReach">{{ item.facebookLink ? Number(item.facebookReach || 0).toLocaleString() : '-' }}</td>
              <td>{{ humanDate(item.uploadedDate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="empty">No uploads found for this sponsor.</p>

    </section>

    <section class="card">
      <h2>Export & Averages</h2>
      <div class="summary-grid">
        <article class="stat">
          <span>Total Views (combined)</span>
          <strong>{{ Number(exportTotals.combined || 0).toLocaleString() }}</strong>
        </article>
        <article class="stat">
          <span>Average Views</span>
          <strong>{{ Number(averages.avgCombined || 0).toLocaleString() }}</strong>
        </article>
        <article class="stat">
          <span>Average YouTube</span>
          <strong class="yt-color">{{ Number(averages.avgYT || 0).toLocaleString() }}</strong>
        </article>
        <article class="stat">
          <span>Average Facebook</span>
          <strong class="fb-color">{{ Number(averages.avgFB || 0).toLocaleString() }}</strong>
        </article>
      </div>
    </section>

    <section class="card">
      <h2>Views </h2>
      <p v-if="views.isLocked" class="locked">Views available after {{ humanDateTime(views.unlockAt) }} ({{ views.delayDays }} day delay).</p>
      <p v-else-if="views.status === 'unavailable'" class="locked">Views are currently unavailable.</p>
      <div v-else>
        <div class="views-stats">
          <article class="stat stat-total">
            <span>Total Views</span>
            <strong class="total-color">{{ Number((views.youtubeTotalViews || 0) + (views.facebookTotalViews || 0)).toLocaleString() }}</strong>
          </article>
          <article class="stat">
            <span>YouTube Views</span>
            <strong class="yt-color">{{ Number(views.youtubeTotalViews || 0).toLocaleString() }}</strong>
          </article>
          <article class="stat">
            <span>Facebook Views</span>
            <strong class="fb-color">{{ Number(views.facebookTotalViews || 0).toLocaleString() }}</strong>
          </article>
          <article v-if="views.facebookTotalReach > 0" class="stat">
            <span>Facebook Reach</span>
            <strong class="fb-reach-color">{{ Number(views.facebookTotalReach || 0).toLocaleString() }}</strong>
          </article>
        </div>
        <div class="platform-selector">
          <button :class="['platform-btn', chartPlatform === 'both' ? 'active' : '']" @click="selectPlatform('both')">Both</button>
          <button :class="['platform-btn yt-btn', chartPlatform === 'youtube' ? 'active' : '']" @click="selectPlatform('youtube')">YouTube</button>
          <button :class="['platform-btn fb-btn', chartPlatform === 'facebook' ? 'active' : '']" @click="selectPlatform('facebook')">Facebook</button>
        </div>
        <p v-if="views.warnings && views.warnings.length" class="locked">{{ views.warnings[0] }}</p>
        <div class="chart-wrap">
          <canvas ref="viewsChart"></canvas>
        </div>
      </div>
    </section>

    <p v-if="message" class="message">{{ message }}</p>
  </div>
</template>

<script>
import Chart from 'chart.js';
import * as XLSX from 'xlsx';
import sponsorService from '../services/sponsorService';

export default {
  name: 'PublicSponsorReport',
  data() {
    return {
      report: {
        sponsor: {},
        summary: {},
        campaignSummaries: [],
        selectedCampaignSummary: null,
        uploads: [],
        views: {},
      },
      message: '',
      chartInstance: null,
      loading: false,
      chartPlatform: 'both',
      filters: {
        campaignId: '',
        dateFrom: '',
        dateTo: '',
      },
    };
  },
  computed: {
    // totals and averages for export/summary
    exportTotals() {
      const rows = Array.isArray(this.uploads) ? this.uploads : [];
      let totalYT = 0;
      let totalFB = 0;
      let combined = 0;
      let rowsWithViews = 0;
      rows.forEach(r => {
        const yt = Number(r.youtubeViews || 0);
        const fb = Number(r.facebookViews || 0);
        const any = (yt + fb) > 0;
        totalYT += yt;
        totalFB += fb;
        combined += yt + fb;
        if (any) rowsWithViews += 1;
      });
      return { totalYT, totalFB, combined, rowsWithViews };
    },
    averages() {
      const t = this.exportTotals;
      const denom = t.rowsWithViews || 1;
      return {
        avgCombined: denom ? Math.round(t.combined / denom) : 0,
        avgYT: denom ? Math.round(t.totalYT / denom) : 0,
        avgFB: denom ? Math.round(t.totalFB / denom) : 0,
      };
    },
    summary() {
      return this.report.summary || {};
    },
    uploads() {
      return Array.isArray(this.report.uploads) ? this.report.uploads : [];
    },
    views() {
      return this.report.views || {};
    },
    campaignOptions() {
      return Array.isArray(this.report.campaignSummaries) ? this.report.campaignSummaries : [];
    },
    selectedCampaignSummary() {
      return this.report.selectedCampaignSummary || null;
    },
    hasFacebookReach() {
      return this.uploads.some(item => (item.facebookReach || 0) > 0);
    },
  },
  mounted() {
    this.loadReport();
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },
  methods: {
    async loadReport() {
      this.loading = true;
      this.message = '';
      const token = this.$route.params.token;
      if (!token) {
        this.message = 'Invalid report link';
        this.loading = false;
        return;
      }

      try {
        const response = await sponsorService.getPublicSponsorReport(token, this.filters);
        this.report = response.item || this.report;
        this.$nextTick(() => this.renderChart());
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load sponsor report';
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = {
        campaignId: '',
        dateFrom: '',
        dateTo: '',
      };
      this.loadReport();
    },
    renderChart() {
      const canvas = this.$refs.viewsChart;
      if (!canvas || this.views.isLocked || this.views.status !== 'ready') return;

      const ytSeries = Array.isArray(this.views.youtubeDailySeries) ? this.views.youtubeDailySeries : [];
      const fbSeries = Array.isArray(this.views.facebookDailySeries) ? this.views.facebookDailySeries : [];

      // Build union of all dates, sorted ascending
      const dateSet = new Set([
        ...ytSeries.map(i => i.date),
        ...fbSeries.map(i => i.date),
      ]);
      const allDates = Array.from(dateSet).sort();

      if (!allDates.length) return;

      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }

      const ytMap = Object.fromEntries(ytSeries.map(i => [i.date, Number(i.views || 0)]));
      const fbViewMap = Object.fromEntries(fbSeries.map(i => [i.date, Number(i.views || 0)]));
      const fbReachMap = Object.fromEntries(fbSeries.map(i => [i.date, Number(i.reach || 0)]));

      const labels = allDates.map(d => this.humanDate(d));
      const datasets = [];

      if (this.chartPlatform === 'both') {
        datasets.push({
          label: 'Total Views',
          data: allDates.map(d => (ytMap[d] || 0) + (fbViewMap[d] || 0)),
          borderColor: '#6a0dad',
          backgroundColor: 'rgba(106, 13, 173, 0.08)',
          pointBackgroundColor: '#6a0dad',
          borderWidth: 2.5,
          lineTension: 0.25,
        });
      }

      if (this.chartPlatform === 'both' || this.chartPlatform === 'youtube') {
        datasets.push({
          label: 'YouTube Views',
          data: allDates.map(d => ytMap[d] || 0),
          borderColor: '#d62828',
          backgroundColor: 'rgba(214, 40, 40, 0.10)',
          pointBackgroundColor: '#d62828',
          lineTension: 0.25,
        });
      }

      if (this.chartPlatform === 'both' || this.chartPlatform === 'facebook') {
        datasets.push({
          label: 'Facebook Views',
          data: allDates.map(d => fbViewMap[d] || 0),
          borderColor: '#1877f2',
          backgroundColor: 'rgba(24, 119, 242, 0.10)',
          pointBackgroundColor: '#1877f2',
          lineTension: 0.25,
        });
      }

      if (this.chartPlatform === 'facebook') {
        const fbReachValues = allDates.map(d => fbReachMap[d] || 0);
        const hasReachData = fbReachValues.some(v => v > 0);
        if (hasReachData) {
          datasets.push({
            label: 'Facebook Reach',
            data: fbReachValues,
            borderColor: '#27ae60',
            backgroundColor: 'rgba(39, 174, 96, 0.08)',
            pointBackgroundColor: '#27ae60',
            borderDash: [5, 4],
            lineTension: 0.25,
          });
        }
      }

      if (!datasets.length) return;

      this.chartInstance = new Chart(canvas, {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: true },
          scales: {
            yAxes: [{ ticks: { beginAtZero: true } }],
          },
        },
      });
    },
    selectPlatform(platform) {
      this.chartPlatform = platform;
      this.$nextTick(() => this.renderChart());
    },
    humanDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    humanDateTime(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString();
    },
    // Export helpers
    buildExportRows() {
      const rows = [];
      const header = ['Sl', 'Date', 'Title', 'YT Link', 'Views (YT)', 'FB Link', 'Views (FB)'];
      rows.push(header);
      (Array.isArray(this.uploads) ? this.uploads : []).forEach((item, idx) => {
        rows.push([
          idx + 1,
          this.humanDate(item.uploadedDate),
          item.title || '',
          item.youtubeLink || '',
          Number(item.youtubeViews || 0),
          item.facebookLink || '',
          Number(item.facebookViews || 0),
        ]);
      });

      // subtotal row
      const totals = this.exportTotals;
      rows.push(['', '', 'Subtotal', '', totals.totalYT, '', totals.totalFB]);
      return rows;
    },
    downloadXlsx() {
      try {
        const aoa = this.buildExportRows();
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sponsor Report');
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const sponsorName = (this.report.sponsor && this.report.sponsor.sponsorName) ? this.report.sponsor.sponsorName.replace(/[^a-z0-9]/gi, '_') : 'sponsor_report';
        a.download = `${sponsorName}_report.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('XLSX export error', err);
        this.message = 'Failed to generate XLSX file.';
      }
    },
    downloadCsv() {
      try {
        const rows = this.buildExportRows();
        const csv = rows.map(r => r.map(field => {
          if (field === null || field === undefined) return '';
          const s = String(field);
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return '"' + s.replace(/"/g, '""') + '"';
          }
          return s;
        }).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const sponsorName = (this.report.sponsor && this.report.sponsor.sponsorName) ? this.report.sponsor.sponsorName.replace(/[^a-z0-9]/gi, '_') : 'sponsor_report';
        a.download = `${sponsorName}_report.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('CSV export error', err);
        this.message = 'Failed to generate CSV file.';
      }
    },
  },
};
</script>

<style scoped>
.public-report-page { min-height: 100vh; padding: 20px; background: linear-gradient(145deg, #fefcf7 0%, #f6fbff 100%); }
.hero { margin-bottom: 12px; }
.hero h1 { margin: 0; color: #1f2d3a; }
.hero p { margin: 6px 0 0; color: #5d7384; }
.card { background: #fff; border: 1px solid #d8e1e8; border-radius: 12px; padding: 14px; margin-bottom: 12px; }
.filters-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; align-items: end; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field label { font-size: 12px; color: #5f7389; font-weight: 700; }
.field input, .field select { border: 1px solid #cdd9e8; border-radius: 8px; padding: 8px 10px; font-size: 12px; }
.actions { display: flex; gap: 8px; }
.btn { border: 1px solid #0e8e7f; background: #12a594; color: #fff; border-radius: 8px; padding: 8px 12px; font-weight: 700; cursor: pointer; }
.btn.clear { border-color: #607382; background: #71889b; }
.btn:disabled { opacity: 0.65; cursor: not-allowed; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.campaign-summary-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); }
.stat { border: 1px solid #e1e9ef; border-radius: 10px; padding: 10px; background: #f8fcff; }
.stat span { display: block; font-size: 12px; color: #5f7389; }
.stat strong { display: block; margin-top: 6px; color: #223647; font-size: 18px; }
.table-wrap { overflow-x: auto; border: 1px solid #d8e8e4; border-radius: 10px; }
.upload-table { width: 100%; min-width: 900px; border-collapse: collapse; }
.upload-table th, .upload-table td { padding: 8px; border-bottom: 1px solid #e6f0ee; text-align: left; font-size: 12px; }
.upload-table th { background: #f4faf8; color: #24475b; }
.upload-table a { color: #0f6aa8; font-weight: 700; }
.chart-wrap { height: 280px; }
.views-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 10px; margin-bottom: 12px; }
.stat-total { border-left: 3px solid #6a0dad; }
.total-color { color: #6a0dad; }
.yt-color { color: #d62828; }
.fb-color { color: #1877f2; }
.fb-reach-color { color: #27ae60; }
.platform-selector { display: flex; gap: 8px; margin-bottom: 12px; }
.platform-btn { border: 1px solid #b0bec5; background: #f4f8fb; color: #3a5068; border-radius: 20px; padding: 5px 16px; font-size: 12px; font-weight: 700; cursor: pointer; transition: background 0.15s, color 0.15s; }
.platform-btn.active { background: #1f2d3a; color: #fff; border-color: #1f2d3a; }
.platform-btn.yt-btn.active { background: #d62828; border-color: #d62828; }
.platform-btn.fb-btn.active { background: #1877f2; border-color: #1877f2; }
.youtube-total { color: #223647; font-size: 14px; }
.locked { color: #8a5a23; font-weight: 700; }
.empty, .message { color: #647a8d; }
@media (max-width: 768px) {
  .summary-grid { grid-template-columns: 1fr; }
  .filters-grid { grid-template-columns: 1fr; }
  .actions { flex-direction: column; }
  .public-report-page { padding: 14px; }
}
</style>
